import {
  Box,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  Tooltip,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { serverActionService } from '../../../services/serverActionService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { DeleteRounded } from '@mui/icons-material'
import { toast } from 'react-toastify'
import ConfirmationDialog from '../Workflow/components/ConfirmationDialog'
import ModeSharpIcon from '@mui/icons-material/ModeSharp'
import AddOutboundServerAction from './components/AddOutboundServerAction'
import SetupKeyValuePair from './components/SetupKeyValuePair'
import { GroupHeader, GroupItems } from '../components/CreateProcessTemplateDialog'
import SetupInboundTranslation from './components/SetupInboundTranslation'
import KeyIcon from '@mui/icons-material/Key'

export const ACTION_TYPE = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
}

const ServerActionConfig = () => {
  const { user } = useAuthContext()
  const [actionType, setActionType] = useState(ACTION_TYPE.OUTBOUND)
  const [inboundActionTranslations, setInboundActionTranslations] = useState([])
  const [outboundActions, setOutboundActions] = useState([])
  const [activities, setActivities] = useState([])
  const [routes, setRoutes] = useState([])
  const [deletingOutboundAction, setDeletingOutboundAction] = useState(null)
  const [showAddOutboundAction, setShowOutboundServerAction] = useState(false)
  const [newServerAction, setNewServerAction] = useState({ http_request_method: 'POST' })
  const [keyValuePairsForAction, showKeyValuePairsForAction] = useState(null)
  const [keyFilter, setKeyFilter] = useState({})
  const [showSetupKeyDialog, setShowSetupKeyDialog] = useState(false)

  useEffect(() => {
    ;(async () => {
      if (!user) return
      const actions = await serverActionService.getActions(user)
      setInboundActionTranslations(actions.inboundTranslations)
      setOutboundActions(actions.outbound)
      setActivities(actions.activities)
      setRoutes(actions.routes)
      setRoutes(actions.routes)
    })()
  }, [user, newServerAction])

  const deleteOutboundServerAction = async (id) => {
    try {
      await serverActionService.deleteAction(user, id)
      setDeletingOutboundAction(null)
      setOutboundActions(outboundActions.filter((it) => it.id !== id))
      toast.success('Deleted successfully')
    } catch (error) {
      toast.error(`Something went wrong!`)
    }
  }

  const addOutboundServerAction = async (newServerAction) => {
    try {
      let jsonHeaders = {}
      try {
        jsonHeaders = JSON.parse(newServerAction.headers)
      } catch (error) {
        return toast.error(`Headers is not a valid json object!`)
      }
      await serverActionService.addNewAction(user, {
        ...newServerAction,
        headers: jsonHeaders,
      })
      toast.success('Added successfully')
    } catch (error) {
      toast.error(`Something went wrong!`)
    }
  }

  return (
    <Box>
      <Box style={{ display: 'flex', gap: 16 }}>
        <Typography variant="h5" fontWeight="bold">
          Server Actions
        </Typography>
        <Select size="small" value={actionType} onChange={(e) => setActionType(e.target.value)}>
          <MenuItem value={ACTION_TYPE.OUTBOUND}>Outbound</MenuItem>
          <MenuItem value={ACTION_TYPE.INBOUND}>Inbound</MenuItem>
        </Select>
      </Box>

      {actionType == ACTION_TYPE.OUTBOUND ? (
        <Box style={{ marginTop: 16 }}>
          <TableContainer style={{ backgroundColor: 'white' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action Label</TableCell>
                  <TableCell>Trigger Activity</TableCell>
                  <TableCell>Trigger Route</TableCell>
                  <TableCell>Method | Endpoint</TableCell>
                  <TableCell>Headers</TableCell>
                  <TableCell>Sequence</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outboundActions?.map((outbound) => {
                  return (
                    <TableRow>
                      <TableCell style={{ padding: 4 }}>{outbound.label}</TableCell>
                      <TableCell style={{ padding: 4 }}>
                        {
                          activities.find((it) => it.id === outbound.trigger_activity_template_id)
                            ?.activity_template_name
                        }
                      </TableCell>
                      <TableCell style={{ padding: 4 }}>
                        {
                          routes.find((it) => it.id === outbound.trigger_route_template_id)
                            ?.route_template_name
                        }
                      </TableCell>
                      <TableCell style={{ padding: 4 }}>
                        {outbound.http_request_method} | {outbound.endpoint}
                      </TableCell>
                      <TableCell style={{ padding: 4 }}>
                        {JSON.stringify(outbound.headers)}
                      </TableCell>
                      <TableCell style={{ padding: 4 }}>{outbound.sequence}</TableCell>
                      <TableCell style={{ padding: 4 }}>
                        <Box style={{ display: 'flex' }}>
                          <Tooltip title="Delete Action">
                            <IconButton onClick={() => setDeletingOutboundAction(outbound)}>
                              <DeleteRounded style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add/Edit Field Keys">
                            <IconButton onClick={() => showKeyValuePairsForAction(outbound)}>
                              <KeyIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Server Action">
                            <IconButton
                              onClick={() => {
                                setNewServerAction({
                                  ...outbound,
                                  headers: JSON.stringify(outbound.headers),
                                })
                                setShowOutboundServerAction(true)
                              }}
                            >
                              <ModeSharpIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            style={{ marginTop: 16 }}
            color="primary"
            variant="contained"
            onClick={() => setShowOutboundServerAction(true)}
          >
            Add Outbound Server Action
          </Button>
        </Box>
      ) : (
        <Box style={{ marginTop: 16 }}>
          <Autocomplete
            style={{ marginBottom: 8 }}
            id="select-derived-field"
            options={activities || []}
            size="small"
            getOptionLabel={(item) =>
              item?.process_template_name + ' / ' + item?.activity_template_name
            }
            value={activities?.find((it) => it.id == keyFilter.activity_template_id)}
            onChange={(_, v) =>
              setKeyFilter({
                ...keyFilter,
                activity_template_id: v?.id,
              })
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Select Activity" color="primary" />
            )}
            groupBy={(option) => option.process_template_name}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )
            }}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Process</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Key</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inboundActionTranslations
                  ?.filter((it) => {
                    return keyFilter.activity_template_id == it.activity_template_id
                  })
                  .map((translation) => {
                    return (
                      <TableRow>
                        <TableCell style={{ padding: 4 }}>
                          {translation.process_template_name}
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          {translation.activity_template_name}
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          {translation.field_template_name}
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>{translation.key}</TableCell>
                      </TableRow>
                    )
                  })}
                <TableRow></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            variant="contained"
            onClick={() => setShowSetupKeyDialog(true)}
            color="primary"
            style={{ marginTop: 16 }}
          >
            Setup Keys
          </Button>
        </Box>
      )}

      <ConfirmationDialog
        title={`Delete outbound action associated with '${deletingOutboundAction?.activity_template_name}'`}
        message={'Be careful! It will also delete all translation key associated with this action.'}
        open={deletingOutboundAction != null}
        onClose={() => setDeletingOutboundAction(null)}
        onConfirm={() => deleteOutboundServerAction(deletingOutboundAction?.id)}
        reloadProcesses={() => {
          setOutboundActions(
            outboundActions?.filter((it) => it.id != deletingOutboundAction?.id) || []
          )
        }}
      />

      <SetupKeyValuePair
        keyValuePairsForAction={keyValuePairsForAction}
        showKeyValuePairsForAction={showKeyValuePairsForAction}
      />

      <AddOutboundServerAction
        setNewServerAction={setNewServerAction}
        addOutboundServerAction={addOutboundServerAction}
        setShowOutboundServerAction={setShowOutboundServerAction}
        newServerAction={newServerAction}
        activities={activities}
        routes={routes}
        showAddOutboundAction={showAddOutboundAction}
      />

      <SetupInboundTranslation
        open={showSetupKeyDialog}
        onClose={() => setShowSetupKeyDialog(false)}
        fields={
          inboundActionTranslations?.filter(
            (it) => it.activity_template_id == keyFilter.activity_template_id
          ) || []
        }
      />
    </Box>
  )
}

export default ServerActionConfig
